import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { getToken } from "@/lib/api";
import VideoCapture from "@/components/VideoCapture";
import GuidedVideoCapture from "@/components/GuidedVideoCapture";
import VHCImportModal from "@/components/VHCImportModal";
import VhcChecklistModal from "@/components/VhcChecklistModal";
import AdvisorQuestionnaireModal from "@/components/AdvisorQuestionnaireModal";
import ServicePlanQuoteModal from "@/components/ServicePlanQuoteModal";
import JobScheduleEditor from "@/components/JobScheduleEditor";
import CheckInPanel from "@/components/CheckInPanel";
import PreWorkPanel from "@/components/PreWorkPanel";
import CustomerMessagesPanel from "@/components/CustomerMessagesPanel";
import CustomerVideoViewer from "@/components/CustomerVideoViewer";
import CustomerProfilePanel from "@/components/CustomerProfilePanel";
import CsiScorePanel from "@/components/CsiScorePanel";
import CallbackRequestsPanel from "@/components/CallbackRequestsPanel";
import CustomerJourneyTimeline from "@/components/CustomerJourneyTimeline";
import SettingsModal from "@/components/SettingsModal";
import HandoverModal from "@/components/HandoverModal";
import CustomerResponseSummary from "@/components/CustomerResponseSummary";
import StatusUpdateModal from "@/components/StatusUpdateModal";
import CustomerOptOutToggle from "@/components/CustomerOptOutToggle";
import QRCodeButton from "@/components/QRCodeButton";
import SlaCountdown from "@/components/SlaCountdown";
import { AccessoryPicker } from "@/components/AccessoryPicker";
import { buildChapters } from "@/lib/chapters";
import {
  ArrowLeft, Plus, Trash2, Sparkles, Send, Copy, ExternalLink, Gift, Stethoscope,
  Wrench as WrenchIcon, AlertTriangle, Loader2, Check, Library, Search as SearchIcon, Video, Trophy, ScanLine,
  Clock, Zap, ShieldCheck, X, RefreshCcw, Phone, Mail, Calendar, MessageCircle, Edit2, Percent,
} from "lucide-react";
import { toast } from "sonner";

const URGENCIES = [
  { v: "green",  label: "Good",     sub: "Healthy — no action",       color: "bg-success/15 text-success" },
  { v: "amber",  label: "Advisory", sub: "Watch / plan",              color: "bg-warning/15 text-warning" },
  { v: "red",    label: "Requires attention", sub: "Urgent / safety",  color: "bg-destructive/15 text-destructive" },
];

function DiscountRow({ job, onChanged }) {
  const applied = job.manual_discount || null;
  const [open, setOpen] = useState(false);
  const [pct, setPct] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const apply = async (percent) => {
    const p = Number(percent);
    if (!(p > 0)) return;
    setBusy(true);
    try {
      await api.post(`/jobs/${job.id}/discount`, { pct: p, reason });
      toast.success(`${p}% discount applied.`);
      setOpen(false);
      setPct(""); setReason("");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't apply discount.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/jobs/${job.id}/discount`);
      toast.success("Discount removed.");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove discount.");
    } finally {
      setBusy(false);
    }
  };

  if (applied) {
    return (
      <div className="border-t border-border pt-2 flex items-center justify-between gap-3 text-xs" data-testid="discount-applied-row">
        <div className="text-muted-foreground">
          Applied by {applied.applied_by || "advisor"} · {applied.applied_at ? new Date(applied.applied_at).toLocaleDateString("en-GB") : ""}
        </div>
        <button
          type="button"
          onClick={remove}
          disabled={busy}
          className="text-xs text-destructive hover:underline disabled:opacity-50"
          data-testid="discount-remove-btn"
        >
          Remove discount
        </button>
      </div>
    );
  }

  if (!open) {
    return (
      <div className="border-t border-border pt-2">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-xs text-primary hover:underline inline-flex items-center gap-1"
          data-testid="discount-open-btn"
        >
          <Percent className="w-3 h-3" /> Apply a discount
        </button>
      </div>
    );
  }

  return (
    <div className="border-t border-border pt-3 space-y-2" data-testid="discount-picker">
      <div className="label-overline">Apply discount</div>
      <div className="flex flex-wrap gap-1.5">
        {[5, 10, 15, 20].map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => apply(p)}
            disabled={busy}
            className="text-xs px-3 py-1.5 rounded-full border border-border bg-background/40 hover:bg-warning/15 hover:border-warning/40 hover:text-warning transition-colors"
            data-testid={`discount-quick-${p}`}
          >
            {p}%
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr_auto] gap-2">
        <div className="relative">
          <input
            type="number"
            min="0"
            max="50"
            step="0.5"
            value={pct}
            onChange={(e) => setPct(e.target.value)}
            placeholder="Custom"
            className="input-default text-sm h-9 w-full pr-6"
            data-testid="discount-custom-input"
          />
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
        </div>
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (loyalty, goodwill, promo…)"
          className="input-default text-sm h-9 w-full"
          data-testid="discount-reason-input"
        />
        <div className="flex gap-1.5">
          <button type="button" onClick={() => setOpen(false)} className="btn-outline text-xs h-9" data-testid="discount-cancel">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => apply(pct)}
            disabled={busy || !Number(pct)}
            className="btn-primary text-xs h-9 inline-flex items-center gap-1"
            data-testid="discount-custom-apply"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Percent className="w-3.5 h-3.5" />}
            Apply
          </button>
        </div>
      </div>
      <div className="text-[11px] text-muted-foreground">
        Advisors can apply up to 10%. Discounts above 10% need a workshop owner or manager.
      </div>
    </div>
  );
}

function JobSidePanels({ job, onChanged }) {
  // Two collapsible cards on the customer sheet:
  //   1. Diagnostic questionnaire — same modal PreCalls uses (advisor mode)
  //   2. EMaC service plan — status + one-tap re-check + quote-a-plan
  const [showAdvQ, setShowAdvQ] = useState(false);
  const [showPlanQuote, setShowPlanQuote] = useState(false);
  const [emacBusy, setEmacBusy] = useState(false);

  const qAnswered = !!job.questionnaire_submitted_at;
  const planLive = !job.service_plan_opportunity && !!job.service_plan_status;
  const planStatus = job.service_plan_status;
  const planCheckedAt = job.service_plan_checked_at;
  const lastQuoteAt = job.last_service_plan_quote_at;
  const quoteCount = (job.service_plan_quotes || []).length;

  const recheckEmac = async () => {
    setEmacBusy(true);
    try {
      const r = await api.get(`/emac/lookup?reg=${encodeURIComponent(job.vehicle_reg || "")}&refresh=1`);
      toast.success(r.data.found ? "EMaC plan found — refreshed." : "No live plan on record.");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "EMaC lookup failed.");
    } finally {
      setEmacBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {/* Diagnostic questionnaire — fill in with customer on the call */}
      <div className="card-default p-5" data-testid="jobeditor-diagnostic-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-warning/15 text-warning grid place-items-center shrink-0">
            <Stethoscope className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="label-overline text-warning">Diagnostic intake</div>
            <div className="font-display font-bold text-base mt-0.5">
              {qAnswered ? "Answers on file" : "Fill in with customer"}
            </div>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              {qAnswered
                ? `Filled ${new Date(job.questionnaire_submitted_at).toLocaleDateString("en-GB")}${
                    job.questionnaire_submitted_by_advisor ? " · advisor mode" : " · by customer"
                  }`
                : "10 quick questions to capture symptoms — helps the tech go straight to the right check."}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setShowAdvQ(true)}
          className="btn-outline w-full text-xs mt-3 inline-flex items-center justify-center gap-1.5"
          data-testid="jobeditor-diagnostic-open"
        >
          <Stethoscope className="w-3.5 h-3.5" />
          {qAnswered ? "Review / edit answers" : "Fill in with customer"}
        </button>
      </div>

      {/* EMaC service plan status */}
      <div className="card-default p-5" data-testid="jobeditor-emac-card">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg grid place-items-center shrink-0 ${
            planLive ? "bg-success/15 text-success" : "bg-warning/15 text-warning"
          }`}>
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className={`label-overline ${planLive ? "text-success" : "text-warning"}`}>
              EMaC service plan
            </div>
            <div className="font-display font-bold text-base mt-0.5">
              {planLive ? (planStatus || "Live plan on file")
                : quoteCount > 0 ? `Quoted · awaiting response`
                : "No live plan — opportunity"}
            </div>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              {planCheckedAt
                ? `Last checked ${new Date(planCheckedAt).toLocaleDateString("en-GB")}`
                : "Not yet checked with EMaC — tap Re-check to look up."}
              {lastQuoteAt && !planLive && ` · Last quote sent ${new Date(lastQuoteAt).toLocaleDateString("en-GB")}`}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 mt-3">
          <button
            type="button"
            onClick={recheckEmac}
            disabled={emacBusy}
            className="btn-outline text-xs inline-flex items-center justify-center gap-1.5"
            data-testid="jobeditor-emac-recheck"
          >
            {emacBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />}
            Re-check
          </button>
          <button
            type="button"
            onClick={() => setShowPlanQuote(true)}
            disabled={planLive}
            className="btn-primary text-xs inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
            data-testid="jobeditor-emac-quote"
            title={planLive ? "This vehicle already has a live plan — no quote needed" : "Send a service plan quote"}
          >
            <Sparkles className="w-3.5 h-3.5" />
            {planLive ? "Already covered" : "Quote a plan"}
          </button>
        </div>
      </div>

      <AdvisorQuestionnaireModal
        jobId={job.id}
        open={showAdvQ}
        customerName={job.customer_name}
        onClose={() => setShowAdvQ(false)}
        onSaved={() => onChanged?.()}
      />
      <ServicePlanQuoteModal
        job={job}
        open={showPlanQuote}
        onClose={() => setShowPlanQuote(false)}
        onSent={() => onChanged?.()}
      />
    </div>
  );
}

export default function JobEditorPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingNotes, setSavingNotes] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [videoBusy, setVideoBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [showAddItem, setShowAddItem] = useState(false);
  const [showAccessories, setShowAccessories] = useState(false);
  const [aiVhcBusy, setAiVhcBusy] = useState(false);
  const [aiVhcDraft, setAiVhcDraft] = useState(null);
  const [aiVhcExcluded, setAiVhcExcluded] = useState([]);
  const aiVhcPollRef = useRef(false);

  // Poll the background analysis until it finishes. Videos can take a while
  // (stabilise + denoise + whisper + vision on long 4K clips), so we allow a
  // generous 15-minute window and never lose a finished draft — it's saved on
  // the job server-side either way.
  const startAiVhcPolling = () => {
    if (aiVhcPollRef.current) return;
    aiVhcPollRef.current = true;
    setAiVhcBusy(true);
    const startedAt = Date.now();
    const stop = () => { aiVhcPollRef.current = false; setAiVhcBusy(false); };
    const poll = async () => {
      if (Date.now() - startedAt > 15 * 60 * 1000) {
        toast.info("Analysis is taking longer than usual — it's still running in the background. Check back on this job in a few minutes.");
        stop();
        return;
      }
      try {
        const r = await api.get(`/jobs/${id}/ai-vhc/draft`);
        const { draft, status } = r.data;
        if (status?.state === "failed") {
          toast.error(status.error || "AI couldn't analyse that video");
          stop();
          return;
        }
        if (draft && status?.state === "done") {
          setAiVhcDraft(draft);
          setAiVhcExcluded([]);
          if ((draft.findings || []).length === 0) toast.info("AI watched the video but found no billable items to suggest.");
          else toast.success(`AI found ${draft.findings.length} item${draft.findings.length === 1 ? "" : "s"} in the video`);
          stop();
          return;
        }
      } catch (_) { /* transient — keep polling */ }
      setTimeout(poll, 5000);
    };
    setTimeout(poll, 5000);
  };

  const runAiVhc = async () => {
    setAiVhcBusy(true);
    try {
      await api.post(`/jobs/${id}/ai-vhc/analyse`, {});
      startAiVhcPolling();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI couldn't start the analysis");
      setAiVhcBusy(false);
    }
  };

  // If the advisor left the page mid-analysis (or the old 6-min poll gave
  // up), pick the draft back up from the job on return.
  useEffect(() => {
    const st = job?.ai_vhc_status?.state;
    if (st === "running" && !aiVhcPollRef.current) startAiVhcPolling();
    else if (st === "done" && job?.ai_vhc_draft && !aiVhcDraft) {
      setAiVhcDraft(job.ai_vhc_draft);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, job?.ai_vhc_status?.state]);

  const applyAiVhc = async () => {
    const indexes = (aiVhcDraft?.findings || []).map((_, i) => i).filter((i) => !aiVhcExcluded.includes(i));
    if (indexes.length === 0) { toast.error("Nothing selected"); return; }
    setAiVhcBusy(true);
    try {
      const r = await api.post(`/jobs/${id}/ai-vhc/apply`, { indexes });
      toast.success(`${r.data.applied} item${r.data.applied === 1 ? "" : "s"} added to the quote`);
      setAiVhcDraft(null);
      setAiVhcExcluded([]);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add items");
    } finally { setAiVhcBusy(false); }
  };

  const addAccessory = async (acc) => {
    try {
      await api.post(`/jobs/${id}/items`, {
        type: "accessory",
        name: acc.name,
        description: acc.description || "",
        quantity: 1,
        unit_price: Number(acc.price || 0),
        urgency: "green",
      });
      toast.success(`${acc.name} added to the quote`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add accessory");
    }
  };
  const [showVHC, setShowVHC] = useState(false);
  const [showVhcChecklist, setShowVhcChecklist] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showHandover, setShowHandover] = useState(false);
  const [newItem, setNewItem] = useState({
    type: "labor", name: "", description: "", quantity: 1, unit_price: 0, urgency: "amber", explainer_url: "",
    recommended_in_months: null,
    tyre_budget_name: "", tyre_budget_price: "",
  });
  const [templates, setTemplates] = useState([]);
  const [tplQuery, setTplQuery] = useState("");
  const [savingReady, setSavingReady] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [resendPhone, setResendPhone] = useState("");
  const [resendEmail, setResendEmail] = useState("");
  const [resendBusy, setResendBusy] = useState(false);
  const [preCallBusy, setPreCallBusy] = useState(false);
  const [showStatusUpdate, setShowStatusUpdate] = useState(false);
  const [nowTick, setNowTick] = useState(Date.now());

  useEffect(() => {
    api.get("/repair-templates").then(r => setTemplates(r.data)).catch(() => {});
  }, []);

  const applyTemplate = (t) => {
    setNewItem({
      type: t.type,
      name: t.name,
      description: t.description || "",
      quantity: t.default_quantity,
      unit_price: t.default_price,
      urgency: t.urgency,
      explainer_url: t.explainer_url || "",
      recommended_in_months: t.recommended_in_months ?? null,
    });
    setTplQuery("");
  };

  const refresh = useCallback(async () => {
    try {
      const r = await api.get(`/jobs/${id}`);
      setJob(r.data);
    } catch { toast.error("Job not found"); nav("/dashboard"); }
    finally { setLoading(false); }
  }, [id, nav]);
  useEffect(() => { refresh(); }, [refresh]);

  // Deep-link actions from the Workshop Control panel: ?action=upload-vhc | generate-quote
  useEffect(() => {
    if (!job) return;
    const action = searchParams.get("action");
    if (!action) return;
    if (action === "upload-vhc") {
      setShowVHC(true);
    } else if (action === "generate-quote") {
      generateAI();
    }
    // Strip the param so it doesn't re-fire on subsequent renders
    const next = new URLSearchParams(searchParams);
    next.delete("action");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id]);

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.patch(`/jobs/${id}`, { technician_notes: job.technician_notes || "" });
      toast.success("Saved");
    } catch { toast.error("Failed to save"); }
    finally { setSavingNotes(false); }  };

  const saveReady = async () => {
    setSavingReady(true);
    try {
      const r = await api.patch(`/jobs/${id}`, { estimated_ready_text: (job.estimated_ready_text || "").trim() });
      const notif = r.data?._notification_log || [];
      const sent = notif.filter((l) => l.ok).map((l) => l.channel.toUpperCase());
      if (sent.length) {
        toast.success(`New ready time saved — customer apology sent via ${sent.join(" + ")}`);
      } else {
        toast.success(job.estimated_ready_text ? "Ready time saved · customer will see it on the quote" : "Cleared");
      }
    } catch { toast.error("Failed to save"); }
    finally { setSavingReady(false); }
  };

  const uploadVideo = async (file, chapters = []) => {
    setVideoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (chapters && chapters.length) fd.append("chapters", JSON.stringify(chapters));
      await api.post(`/jobs/${id}/video`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(chapters.length ? `Video saved · ${chapters.length} chapters` : "Video saved");
      refresh();
    } catch (e) { toast.error("Upload failed"); }
    finally { setVideoBusy(false); }
  };

  const addItem = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...newItem,
        quantity: Number(newItem.quantity),
        unit_price: Number(newItem.unit_price),
        // Only send recommended_in_months for amber items
        recommended_in_months: newItem.urgency === "amber" ? newItem.recommended_in_months : null,
      };
      // Tyre tier alternative — only attach when both fields are filled in.
      const budgetName = (newItem.tyre_budget_name || "").trim();
      const budgetPrice = Number(newItem.tyre_budget_price);
      if (budgetName && budgetPrice > 0) {
        payload.tyre_budget = { name: budgetName, price: budgetPrice };
      }
      // Strip UI-only fields the API doesn't expect
      delete payload.tyre_budget_name;
      delete payload.tyre_budget_price;
      await api.post(`/jobs/${id}/items`, payload);
      setShowAddItem(false);
      setNewItem({ type: "labor", name: "", description: "", quantity: 1, unit_price: 0, urgency: "amber", recommended_in_months: null, tyre_budget_name: "", tyre_budget_price: "" });
      refresh();
    } catch (e) {
      // Surface the actual backend detail (e.g. validation errors) instead of
      // a generic "Failed" toast — saves a round-trip every time the schema
      // and the form drift apart in the future.
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail) && detail[0]?.msg
          ? `${detail[0].msg} (${(detail[0].loc || []).join(".")})`
          : "Couldn't add item — please try again";
      toast.error(msg);
    }
  };

  const removeItem = async (itemId) => {
    try { await api.delete(`/jobs/${id}/items/${itemId}`); refresh(); }
    catch { toast.error("Failed"); }
  };

  const generateAI = async () => {
    setAiBusy(true);
    try {
      await api.post(`/jobs/${id}/ai-explanation`);
      toast.success("Explanation generated");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI failed");
    } finally { setAiBusy(false); }
  };

  const sendToCustomer = async () => {
    setSending(true);
    try {
      const r = await api.post(`/jobs/${id}/send`);
      const log = r.data?.dispatch_log || [];
      const sent = log.filter((l) => l.ok).map((l) => l.channel.toUpperCase());
      const failed = log.filter((l) => !l.ok).map((l) => l.channel.toUpperCase());
      if (sent.length) {
        toast.success(`Quote sent — ${sent.join(" + ")} dispatched${failed.length ? ` (${failed.join(", ")} failed)` : ""}`);
      } else if (log.length && failed.length) {
        toast.warning(`Share link live, but ${failed.join(", ")} dispatch failed — link copied to clipboard`);
      } else {
        toast.success("Share link live — copied to clipboard (no phone/email on file)");
      }
      refresh();
      const url = `${window.location.origin}/j/${r.data.share_token}`;
      navigator.clipboard?.writeText(url).catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Cannot send"); }
    finally { setSending(false); }
  };

  const markReady = async () => {
    try {
      const r = await api.post(`/jobs/${id}/mark-ready`);
      const log = r.data?._dispatch_log || [];
      const sent = log.filter((l) => l.ok).map((l) => l.channel.toUpperCase());
      const failed = log.filter((l) => !l.ok).map((l) => l.channel.toUpperCase());
      if (sent.length) {
        toast.success(`Car marked ready — customer notified via ${sent.join(" + ")}${failed.length ? ` (${failed.join(", ")} failed)` : ""}`);
      } else if (failed.length) {
        toast.warning(`Marked ready, but ${failed.join(", ")} dispatch failed`);
      } else {
        toast.success("Marked ready — no phone/email on file, so no auto-text sent");
      }
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't mark ready"); }
  };

  // ----- Resend (delayed-dispatch with 5-min cancel window) -----
  const openResend = () => {
    setResendPhone(job?.customer_phone || "");
    setResendEmail(job?.customer_email || "");
    setShowResend(true);
  };

  const submitResend = async () => {
    setResendBusy(true);
    try {
      const r = await api.post(`/jobs/${id}/resend`, {
        customer_phone: resendPhone.trim() || null,
        customer_email: resendEmail.trim() || null,
      });
      const mins = Math.round((r.data?.delay_seconds || 300) / 60);
      toast.success(`Resend queued — fires in ${mins} min. Cancel any time before then.`);
      setShowResend(false);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't queue resend"); }
    finally { setResendBusy(false); }
  };

  const cancelResend = async () => {
    try {
      await api.post(`/jobs/${id}/resend/cancel`);
      toast.success("Resend cancelled — no message will be sent");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't cancel"); }
  };

  // ----- Pre-call (fires the day-before reminder SMS + email on demand) -----
  // Available on any job that has a phone or email — not just diary-imports.
  const sendPreCall = async () => {
    setPreCallBusy(true);
    try {
      const channels = [];
      if ((job.customer_phone || "").trim()) channels.push("sms");
      if ((job.customer_email || "").trim()) channels.push("email");
      if (channels.length === 0) {
        toast.error("Add a phone or email first");
        return;
      }
      // Fire each channel via the existing per-job endpoint. We do them in
      // sequence so the second response carries the latest pre_call_sent_at.
      const sent = [];
      const failed = [];
      for (const channel of channels) {
        try {
          const r = await api.post(`/jobs/${id}/send-pre-call`, { channel });
          if (r.data?.ok) sent.push(channel.toUpperCase());
          else failed.push(channel.toUpperCase());
        } catch { failed.push(channel.toUpperCase()); }
      }
      if (sent.length) {
        toast.success(`Pre-call sent — ${sent.join(" + ")}${failed.length ? ` (${failed.join(", ")} failed)` : ""}`);
      } else {
        toast.error(`Pre-call failed — ${failed.join(", ")}`);
      }
      refresh();
    } finally { setPreCallBusy(false); }
  };

  // Tick every second while a resend is queued so the countdown stays live.
  // Once the scheduled time passes we trigger a refresh to pick up dispatch
  // results / the cleared pending_resend.
  useEffect(() => {
    if (!job?.pending_resend?.scheduled_at) return;
    const tick = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [job?.pending_resend?.scheduled_at]);

  useEffect(() => {
    const sched = job?.pending_resend?.scheduled_at;
    if (!sched) return;
    const remaining = new Date(sched).getTime() - nowTick;
    if (remaining < -2000) refresh(); // dispatch should have fired by now
  }, [nowTick, job?.pending_resend?.scheduled_at, refresh]);

  // Open the handover checklist instead of completing directly
  const completeJob = () => setShowHandover(true);

  if (loading || !job) {
    return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  }

  const shareUrl = `${window.location.origin}/j/${job.share_token}`;
  const videoSrc = job.video_path
    ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${job.video_path}?auth=${encodeURIComponent(getToken() || "")}`
    : null;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card sticky top-0 z-30">
        <div className="max-w-7xl xl:max-w-[1500px] 2xl:max-w-[1720px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="btn-outline" data-testid="back-to-dashboard-btn">
              <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Dashboard</span>
            </Link>
            <div className="hidden md:block">
              <div className="label-overline">Job</div>
              <div className="font-display font-bold text-lg leading-none">{job.customer_name || "Unnamed"}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded text-xs font-bold uppercase tracking-wider ${
              job.status === "draft" ? "bg-secondary text-secondary-foreground" :
              job.status === "sent" ? "bg-primary/10 text-primary" :
              job.status === "viewed" ? "bg-warning/10 text-warning" :
              job.status === "approved" ? "bg-success/10 text-success" :
              job.status === "completed" ? "bg-success text-success-foreground" :
              job.status === "rejected" ? "bg-destructive/10 text-destructive" :
              "bg-warning/10 text-warning"
            }`}>{job.status}</span>
            {["approved", "partial", "viewed", "rejected"].includes(job.status) && !job.ready_for_collection_at && (
              <button onClick={markReady} className="btn-outline" data-testid="mark-ready-btn" title="Tell the customer their car is ready to collect">
                <Check className="w-4 h-4" />
                Mark ready
              </button>
            )}
            {job.ready_for_collection_at && job.status !== "completed" && (
              <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-primary/15 text-primary inline-flex items-center gap-1" data-testid="ready-chip">
                <Check className="w-3 h-3" /> Ready
              </span>
            )}
            {["approved", "partial", "viewed", "rejected"].includes(job.status) && (
              <button onClick={completeJob} className="btn-outline" data-testid="complete-job-btn" title="Customer collected — handover & thank-you">
                <Trophy className="w-4 h-4" />
                Customer collected
              </button>
            )}
            {job.status !== "completed" && !job.pre_call_sent_at && (job.customer_phone || job.customer_email) && (
              <button
                onClick={sendPreCall}
                disabled={preCallBusy}
                className="btn-outline"
                data-testid="send-precall-btn"
                title="Fire the pre-call reminder SMS + email now"
              >
                {preCallBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
                Send pre-call
              </button>
            )}
            {job.pre_call_sent_at && (
              <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-success/10 text-success inline-flex items-center gap-1" data-testid="precall-sent-chip" title={`Pre-call sent ${new Date(job.pre_call_sent_at).toLocaleString()}`}>
                <Check className="w-3 h-3" /> Pre-call sent
              </span>
            )}
            {job.status !== "completed" && job.status === "draft" && (
              <button onClick={sendToCustomer} disabled={sending || (job.items || []).length === 0} className="btn-primary" data-testid="send-job-btn">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send to customer
              </button>
            )}
            {job.status !== "completed" && job.status !== "draft" && !job?.pending_resend?.scheduled_at && (
              <button onClick={openResend} disabled={(job.items || []).length === 0} className="btn-outline" data-testid="resend-job-btn" title="Resend the quote (5-minute cancel window)">
                <RefreshCcw className="w-4 h-4" />
                Resend quote
              </button>
            )}
          </div>
        </div>
        {job?.pending_resend?.scheduled_at && (
          <ResendCountdownBanner
            scheduledAt={job.pending_resend.scheduled_at}
            phone={job.pending_resend.customer_phone}
            email={job.pending_resend.customer_email}
            nowTick={nowTick}
            onCancel={cancelResend}
          />
        )}
      </header>

      <main className="max-w-7xl xl:max-w-[1500px] 2xl:max-w-[1720px] mx-auto px-6 py-8 grid md:grid-cols-12 gap-6">
        {/* Left column */}
        <div className="md:col-span-7 space-y-6">
          <CustomerMessagesPanel jobId={id} />
          <CustomerProfilePanel job={job} />
          <CsiScorePanel job={job} onChanged={(patch) => setJob((j) => ({ ...j, ...patch }))} />
          <CustomerVideoViewer
            jobId={id}
            videos={job.customer_videos || []}
            onChanged={() => refresh()}
          />
          <CallbackRequestsPanel job={job} onChange={setJob} />
          <CustomerJourneyTimeline job={job} />
          <PreWorkPanel job={job} />
          <CheckInPanel
            job={job}
            onOpenSettings={() => setShowSettings(true)}
            onCheckedIn={(patch) => setJob((j) => ({ ...j, ...patch }))}
          />

          <GuidedVideoCapture
            chapters={buildChapters(job.items || [])}
            onUpload={uploadVideo}
            busy={videoBusy}
          />

          {/* CitNOW video link — alternative to uploaded video */}
          <div className="card-default p-5" data-testid="citnow-card">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-secondary grid place-items-center shrink-0">
                <Video className="w-5 h-5 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="label-overline">CitNOW video link</div>
                <div className="font-display font-bold text-base mt-1">Already filmed in CitNOW?</div>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  Paste the CitNOW share URL — the customer will see it embedded on their quote page, no need to re-record here.
                </p>
                <div className="flex gap-2 mt-3">
                  <input
                    type="url"
                    defaultValue={job.citnow_video_url || ""}
                    placeholder="https://share.citnow.com/…"
                    className="input-default flex-1"
                    data-testid="citnow-url-input"
                    onBlur={async (e) => {
                      const val = e.target.value.trim();
                      if (val === (job.citnow_video_url || "")) return;
                      try {
                        await api.patch(`/jobs/${id}`, { citnow_video_url: val });
                        setJob((j) => ({ ...j, citnow_video_url: val }));
                        if (val) toast.success("CitNOW link saved");
                      } catch (err) {
                        toast.error("Couldn't save link");
                      }
                    }}
                  />
                </div>
                {job.citnow_video_url && (
                  <div className="flex flex-wrap items-center gap-3 mt-2">
                    <a
                      href={job.citnow_video_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                      data-testid="citnow-preview-link"
                    >
                      <Video className="w-3 h-3" /> Preview CitNOW link
                    </a>
                    <button
                      type="button"
                      onClick={runAiVhc}
                      disabled={aiVhcBusy}
                      className="btn-outline text-xs"
                      data-testid="citnow-ai-vhc-btn"
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      {aiVhcBusy ? "AI watching the video… (can take a minute)" : "Build VHC with AI"}
                    </button>
                  </div>
                )}
                {aiVhcDraft && (aiVhcDraft.findings || []).length > 0 && (
                  <div className="mt-3 border border-primary/30 rounded-lg p-3 space-y-2" data-testid="ai-vhc-findings-panel">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary">
                      AI found {aiVhcDraft.findings.length} item{aiVhcDraft.findings.length === 1 ? "" : "s"} in the video — untick any you don't want
                    </div>
                    {aiVhcDraft.findings.map((f, idx) => (
                      <label key={idx} className="flex items-start gap-2 text-xs cursor-pointer" data-testid={`ai-vhc-finding-${idx}`}>
                        <input
                          type="checkbox"
                          checked={!aiVhcExcluded.includes(idx)}
                          onChange={() =>
                            setAiVhcExcluded((ex) => ex.includes(idx) ? ex.filter((i) => i !== idx) : [...ex, idx])
                          }
                          className="mt-0.5 h-3.5 w-3.5"
                        />
                        <span className="flex-1">
                          <span className={`font-bold ${f.urgency === "red" ? "text-destructive" : f.urgency === "amber" ? "text-warning" : "text-success"}`}>
                            {(f.urgency || "amber").toUpperCase()}
                          </span>{" "}
                          <span className="font-semibold">{f.name}</span>
                          {Number(f.unit_price) > 0 && <span className="font-mono-plex"> · £{Number(f.unit_price).toFixed(2)}</span>}
                          {f.price_source === "toyota_trp" && (
                            <span className="ml-1 text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-red-500/10 text-red-600 border border-red-500/30" data-testid={`ai-vhc-trp-badge-${idx}`} title={`Official Toyota fixed repair price · ${f.trp_class || ""}`}>
                              Toyota price
                            </span>
                          )}
                          {f.description && <span className="text-muted-foreground"> — {f.description}</span>}
                        </span>
                      </label>
                    ))}
                    <div className="flex gap-2 pt-1">
                      <button type="button" onClick={applyAiVhc} disabled={aiVhcBusy} className="btn-primary text-xs" data-testid="ai-vhc-apply-btn">
                        Add {aiVhcDraft.findings.length - aiVhcExcluded.length} to quote
                      </button>
                      <button type="button" onClick={() => { setAiVhcDraft(null); setAiVhcExcluded([]); }} className="btn-outline text-xs" data-testid="ai-vhc-dismiss-btn">
                        Dismiss
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {videoSrc && (
            <div className="card-default p-5">
              <div className="label-overline">Current video</div>
              <video src={videoSrc} controls className="w-full rounded-lg mt-3 aspect-video bg-foreground" data-testid="current-video" />
            </div>
          )}

          {/* VHC photo capture — primary entry point for AI auto-extraction */}
          <button
            type="button"
            onClick={() => setShowVHC(true)}
            data-testid="vhc-photo-capture-card"
            className="w-full text-left card-default p-5 border-primary/40 bg-primary/[0.04] hover:bg-primary/[0.07] transition-colors group"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-primary/15 grid place-items-center shrink-0 group-hover:bg-primary/25 transition-colors">
                <ScanLine className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="label-overline text-primary">Snap the VHC sheet · AI auto-extract</div>
                <div className="font-display font-bold text-xl tracking-tight mt-1">
                  Photograph the Vehicle Health Check
                </div>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                  Tap to capture the technician's VHC sheet on your phone — Claude Vision reads every item,
                  grades it red / amber / green, attaches catalogue prices and drops the work straight into the quote below.
                  No typing.
                </p>
                <div className="inline-flex items-center gap-2 mt-3 text-xs font-bold uppercase tracking-wider text-primary">
                  Take photo or upload sheet <span aria-hidden>→</span>
                </div>
              </div>
            </div>
          </button>

          {/* Digital VHC — for workshops that don't run a paper sheet.
              Technician taps green/amber/red across 24 checkpoints, adds a
              note + price on anything flagged, and it lands in the quote. */}
          <button
            type="button"
            onClick={() => setShowVhcChecklist(true)}
            data-testid="vhc-digital-checklist-card"
            className="w-full text-left card-default p-5 border-success/40 bg-success/[0.04] hover:bg-success/[0.08] transition-colors group"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-success/15 grid place-items-center shrink-0 group-hover:bg-success/25 transition-colors">
                <ShieldCheck className="w-6 h-6 text-success" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="label-overline text-success">No paper sheet? Fill it in on screen</div>
                <div className="font-display font-bold text-xl tracking-tight mt-1">
                  Digital VHC · traffic-light checklist
                </div>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                  Walk through 24 standard checkpoints — brakes, tyres, lights, fluids — and tap
                  green, amber or red. Add a quick note and a price on anything flagged.
                  Amber and red items land straight in the customer's quote.
                </p>
                <div className="inline-flex items-center gap-2 mt-3 text-xs font-bold uppercase tracking-wider text-success">
                  Start digital check <span aria-hidden>→</span>
                </div>
              </div>
            </div>
          </button>

          {/* EMaC service plan + Diagnostic questionnaire — both surface here
              so the advisor doesn't have to bounce back to Pre-calls to trigger
              them mid-conversation with the customer. */}
          <JobSidePanels
            job={job}
            onChanged={refresh}
          />
          <JobScheduleEditor job={job} onChanged={refresh} />

          {/* Booked service — the price agreed at booking, before any extras */}
          <div className="card-default p-5" data-testid="booked-service-card">
            <div className="label-overline">As booked</div>
            <div className="font-display font-bold text-base mt-1 flex items-center gap-2">
              <span>Booked service</span>
              <SlaCountdown job={job} size="lg" />
            </div>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              The price the customer agreed at booking. Extras the technician finds are added below — the customer sees the full total on their quote.
            </p>
            <EMaCPlanBadge jobId={id} reg={job.vehicle_reg} onCoverageApplied={refresh} />
            <ToyotaPricingPanel
              jobId={id}
              make={job.vehicle_make}
              model={job.vehicle_model}
              currentName={job.booked_service_name}
              currentAmount={job.booked_service_amount}
              onApplied={(match) => setJob((j) => ({
                ...j,
                booked_service_name: match.name,
                booked_service_amount: match.amount,
              }))}
            />
            <GeelyPricingPanel
              jobId={id}
              make={job.vehicle_make}
              model={job.vehicle_model}
              currentName={job.booked_service_name}
              currentAmount={job.booked_service_amount}
              onApplied={(match) => setJob((j) => ({
                ...j,
                booked_service_name: match.name,
                booked_service_amount: match.amount,
              }))}
            />
            <div className="grid sm:grid-cols-[2fr_1fr] gap-2 mt-3">
              <input
                type="text"
                defaultValue={job.booked_service_name || ""}
                placeholder="e.g. Full Service"
                className="input-default"
                data-testid="booked-service-name-input"
                onBlur={async (e) => {
                  const val = e.target.value.trim();
                  if (val === (job.booked_service_name || "")) return;
                  try {
                    await api.patch(`/jobs/${id}`, { booked_service_name: val });
                    setJob((j) => ({ ...j, booked_service_name: val }));
                  } catch { toast.error("Couldn't save service name"); }
                }}
              />
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  defaultValue={job.booked_service_amount || ""}
                  placeholder="0.00"
                  className="input-default pl-7"
                  data-testid="booked-service-amount-input"
                  onBlur={async (e) => {
                    const raw = e.target.value.trim();
                    const val = raw === "" ? null : Number(raw);
                    if (val === (job.booked_service_amount ?? null)) return;
                    try {
                      await api.patch(`/jobs/${id}`, { booked_service_amount: val });
                      setJob((j) => ({ ...j, booked_service_amount: val }));
                    } catch { toast.error("Couldn't save service price"); }
                  }}
                />
              </div>
            </div>
          </div>

          {/* Items */}
          <div className="card-default p-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="label-overline">Quote</div>
                <div className="font-display font-bold text-xl mt-1">Repairs &amp; services</div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setShowVHC(true)} className="btn-outline" data-testid="vhc-import-btn" title="Upload a Vehicle Health Check sheet">
                  <ScanLine className="w-4 h-4" /> <span className="hidden sm:inline">Import VHC</span>
                </button>
                <button onClick={() => setShowAddItem(true)} className="btn-primary" data-testid="add-item-btn">
                  <Plus className="w-4 h-4" /> Add item
                </button>
                <button onClick={() => setShowAccessories(true)} className="btn-outline" data-testid="add-accessory-btn">
                  <Gift className="w-4 h-4" /> Accessory
                </button>
              </div>
            </div>

            <ToyotaRepairPricingPanel jobId={id} make={job.vehicle_make} model={job.vehicle_model} items={job.items} onAdded={refresh} />

            {(job.items || []).length === 0 ? (
              <div className="mt-6 p-6 border border-dashed border-border rounded-lg text-center text-muted-foreground text-sm">
                No items yet. Add the repairs and services this vehicle needs — each line is the fitted price.
              </div>
            ) : (
              <ul className="mt-4 divide-y divide-border">
                {job.items.map((it) => (
                  <li key={it.id} className="py-4 flex items-start gap-3" data-testid={`item-row-${it.id}`}>
                    <div className="w-9 h-9 rounded-md bg-secondary grid place-items-center shrink-0">
                      <WrenchIcon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold">{it.name}</span>
                        <span className={`text-xs font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${URGENCIES.find(u => u.v === it.urgency)?.color}`}>
                          {it.urgency === "red" && <AlertTriangle className="w-3 h-3 inline mr-0.5" />}
                          {it.urgency}
                        </span>
                      </div>
                      {it.description && <div className="text-sm text-muted-foreground mt-1">{it.description}</div>}
                      <div className="text-xs text-muted-foreground font-mono-plex mt-1">
                        {it.covered_by_plan ? (
                          <span data-testid={`item-covered-${it.id}`}>
                            {it.quantity} × <span className="line-through opacity-70">£{Number(it.covered_by_plan.original_price || 0).toFixed(2)}</span>{" "}
                            = <span className="text-success font-semibold">£0.00</span>
                          </span>
                        ) : (
                          <>{it.quantity} × £{Number(it.unit_price).toFixed(2)} = £{(it.quantity * it.unit_price).toFixed(2)}</>
                        )}
                      </div>
                      {it.covered_by_plan && (
                        <div className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-success bg-success/10 border border-success/30 px-1.5 py-0.5 rounded">
                          <ShieldCheck className="w-3 h-3" />
                          Covered by service plan
                        </div>
                      )}
                      <ItemExplainerEditor
                        jobId={id}
                        item={it}
                        onSaved={(patch) => setJob((j) => ({
                          ...j,
                          items: (j.items || []).map((x) => (x.id === it.id ? { ...x, ...patch } : x)),
                        }))}
                      />
                    </div>
                    <button onClick={() => removeItem(it.id)} className="text-muted-foreground hover:text-destructive p-2" data-testid={`item-remove-${it.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {(job.items || []).length > 0 && (
              <div className="mt-4 border-t border-border pt-4 space-y-2">
                {(job.booked_service_amount || 0) > 0 && (
                  <>
                    <div className="flex justify-between items-baseline text-sm">
                      <span className="text-muted-foreground">{job.booked_service_name || "Booked service"}</span>
                      <span className="font-mono-plex" data-testid="job-booked-amount">£{Number(job.booked_service_amount || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-baseline text-sm">
                      <span className="text-muted-foreground">Additional work (quoted)</span>
                      <span className="font-mono-plex" data-testid="job-extras-subtotal">£{(job.subtotal || 0).toFixed(2)}</span>
                    </div>
                    {job.manual_discount && (
                      <div className="flex justify-between items-baseline text-sm text-warning" data-testid="job-manual-discount-row">
                        <span>{job.manual_discount.pct}% discount{job.manual_discount.reason ? ` — ${job.manual_discount.reason}` : ""}</span>
                        <span className="font-mono-plex">− £{Number(job.manual_discount.amount || 0).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="border-t border-border pt-2 flex justify-between items-baseline">
                      <span className="label-overline">{job.manual_discount ? "After discount" : "If all extras authorised"}</span>
                      <span className="font-display font-bold text-2xl tracking-tight" data-testid="job-grand-if-all">
                        £{Number(job.grand_total ?? (Number(job.booked_service_amount || 0) + Number(job.subtotal || 0))).toFixed(2)}
                      </span>
                    </div>
                  </>
                )}
                {!(job.booked_service_amount > 0) && (
                  <>
                    {job.manual_discount && (
                      <div className="flex justify-between items-baseline text-sm text-warning" data-testid="job-manual-discount-row">
                        <span>{job.manual_discount.pct}% discount{job.manual_discount.reason ? ` — ${job.manual_discount.reason}` : ""}</span>
                        <span className="font-mono-plex">− £{Number(job.manual_discount.amount || 0).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-end items-baseline gap-3">
                      <div className="label-overline">Subtotal</div>
                      <div className="font-display font-bold text-2xl tracking-tight" data-testid="job-subtotal">£{Number(job.grand_total ?? job.subtotal ?? 0).toFixed(2)}</div>
                    </div>
                  </>
                )}
                <DiscountRow
                  job={job}
                  onChanged={refresh}
                />
              </div>
            )}
          </div>

          {/* Tech notes */}
          <div className="card-default p-5">
            <div className="label-overline">Technician notes</div>
            <p className="text-sm text-muted-foreground mt-1">Private notes that help the AI write a great explanation.</p>
            <textarea
              rows={4}
              value={job.technician_notes || ""}
              onChange={(e) => setJob({ ...job, technician_notes: e.target.value })}
              placeholder="e.g. Front pads worn to 2mm, slight pulsing on braking, customer drives 80km/day…"
              className="input-default mt-3"
              data-testid="tech-notes-input"
            />
            <div className="flex justify-end mt-3">
              <button onClick={saveNotes} disabled={savingNotes} className="btn-outline" data-testid="save-notes-btn">
                {savingNotes ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save notes
              </button>
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="md:col-span-5 space-y-6">
          <div className="card-default p-5">
            <div className="label-overline">Customer &amp; vehicle</div>
            <div className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Name</span><span className="font-medium">{job.customer_name || "—"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Email</span><span className="font-medium">{job.customer_email || "—"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Phone</span><span className="font-medium">{job.customer_phone || "—"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Vehicle</span><span className="font-medium">{[job.vehicle_year, job.vehicle_make, job.vehicle_model].filter(Boolean).join(" ") || "—"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Plate</span><span className="font-mono-plex">{job.vehicle_reg || "—"}</span></div>
            </div>
            {job.customer_phone && (
              <div className="mt-3 pt-3 border-t border-border">
                <CustomerOptOutToggle phone={job.customer_phone} />
              </div>
            )}
          </div>

          {/* Estimated ready time — shown on the customer's public quote */}
          <div className="card-default p-5" data-testid="estimated-ready-card">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary" />
              <div className="label-overline">Estimated ready by</div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Optional. Shows on the customer's quote so they know when to pop back.</p>
            <input
              type="text"
              value={job.estimated_ready_text || ""}
              onChange={(e) => setJob({ ...job, estimated_ready_text: e.target.value })}
              placeholder="e.g. 4:30 PM today, or Thu 3pm"
              maxLength={80}
              className="input-default mt-3"
              data-testid="estimated-ready-input"
            />
            <div className="flex justify-end mt-3 gap-2">
              <button onClick={() => setShowStatusUpdate(true)} disabled={!(job.customer_phone || job.customer_email)} className="btn-outline" data-testid="open-status-update-btn" title="Send a templated customer update (waiting parts, on track, almost done, major delay, etc.)">
                <MessageCircle className="w-4 h-4" /> Send update…
              </button>
              <button onClick={saveReady} disabled={savingReady} className="btn-outline" data-testid="save-ready-btn">
                {savingReady ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save
              </button>
            </div>
            {Array.isArray(job.status_update_log) && job.status_update_log.length > 0 && (
              <details className="mt-3 text-xs">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                  {job.status_update_log.length} customer update{job.status_update_log.length === 1 ? "" : "s"} sent
                </summary>
                <ul className="mt-2 space-y-1.5 pl-2 border-l-2 border-border" data-testid="status-update-history">
                  {[...job.status_update_log].reverse().slice(0, 5).map((entry, idx) => (
                    <li key={entry.at || idx} className="pl-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold">{entry.template_label}</span>
                        <span className="text-muted-foreground font-mono-plex text-[10px]">{new Date(entry.at).toLocaleString()}</span>
                      </div>
                      {entry.new_ready_time && <div className="text-muted-foreground">→ {entry.new_ready_time}</div>}
                      <div className="text-[10px] text-muted-foreground">
                        {(entry.channels || []).map((c) => `${c.channel.toUpperCase()}:${c.ok ? "✓" : "✗"}`).join(" · ")}
                      </div>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>

          <div className="card-default p-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="label-overline">AI assistant</div>
                <div className="font-display font-bold text-xl mt-1">Customer explanation</div>
              </div>
              <button onClick={generateAI} disabled={aiBusy} className="btn-primary" data-testid="generate-ai-btn">
                {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {job.ai_explanation ? "Regenerate" : "Generate"}
              </button>
            </div>
            {job.ai_explanation ? (
              <div className="mt-4 p-4 bg-muted/50 rounded-lg text-sm leading-relaxed whitespace-pre-wrap" data-testid="ai-explanation">
                {job.ai_explanation}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground mt-3">
                Add work items and tech notes, then click Generate. Customers will read this alongside the video.
              </p>
            )}
          </div>

          <CustomerResponseSummary job={job} onUpdated={refresh} />

          <div className="card-default p-5">
            <div className="label-overline">Customer link</div>
            <div className="font-mono-plex text-xs break-all bg-muted/50 p-3 rounded mt-2">{shareUrl}</div>
            <div className="flex gap-2 mt-3">
              <button onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Copied"); }} className="btn-outline flex-1" data-testid="copy-link-btn">
                <Copy className="w-4 h-4" /> Copy link
              </button>
              <QRCodeButton
                url={shareUrl}
                label="QR"
                testId="job-share-qr-btn"
                caption={`Hand the customer your phone — they scan, the quote for ${job.customer_name || "this job"} loads instantly.`}
              />
              <a href={shareUrl} target="_blank" rel="noreferrer" className="btn-outline" data-testid="open-link-btn">
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
            {job.customer_comment && (
              <div className="mt-4 p-3 rounded bg-muted/50 text-sm">
                <div className="label-overline">Customer left a comment</div>
                <div className="mt-1 italic">"{job.customer_comment}"</div>
              </div>
            )}
          </div>
        </div>
      </main>

      {showAccessories && (
        <AccessoryPicker
          onAdd={addAccessory}
          onClose={() => setShowAccessories(false)}
          addedNames={(job?.items || []).filter((i) => i.type === "accessory").map((i) => i.name)}
        />
      )}
      {showAddItem && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowAddItem(false)}>
          <form onSubmit={addItem} onClick={(e) => e.stopPropagation()} className="bg-card rounded-lg border border-border shadow-xl w-full max-w-lg p-6 space-y-4 animate-fade-up max-h-[90vh] overflow-y-auto" data-testid="add-item-modal">
            <div className="flex items-center justify-between">
              <div>
                <div className="label-overline">Add</div>
                <h2 className="font-display text-2xl font-bold tracking-tight mt-1">New item</h2>
              </div>
              <Link to="/catalog" target="_blank" className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1" data-testid="manage-catalog-link">
                <Library className="w-3.5 h-3.5" /> Manage catalog
              </Link>
            </div>

            {/* Quick pick from catalog */}
            <div className="border border-border rounded-lg p-3 bg-muted/30">
              <div className="flex items-center gap-2 mb-2">
                <Library className="w-4 h-4 text-primary" />
                <div className="text-xs font-bold uppercase tracking-wider text-foreground">Quick pick from catalog</div>
              </div>
              <div className="relative">
                <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={tplQuery}
                  onChange={(e) => setTplQuery(e.target.value)}
                  placeholder="Type to search e.g. brake, oil, tyre…"
                  className="input-default pl-9"
                  data-testid="quick-pick-input"
                />
              </div>
              {tplQuery && (
                <div className="mt-2 max-h-48 overflow-y-auto border border-border rounded-lg bg-card divide-y divide-border" data-testid="quick-pick-results">
                  {templates
                    .filter(t => {
                      const s = tplQuery.toLowerCase();
                      return t.name.toLowerCase().includes(s) || (t.category || "").toLowerCase().includes(s);
                    })
                    .slice(0, 12)
                    .map(t => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => applyTemplate(t)}
                        className="w-full text-left px-3 py-2 hover:bg-muted/50 transition-colors flex items-center gap-2"
                        data-testid={`quick-pick-${t.id}`}
                      >
                        <div className="w-7 h-7 rounded bg-secondary grid place-items-center shrink-0">
                          <WrenchIcon className="w-3.5 h-3.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{t.name}</div>
                          <div className="text-xs text-muted-foreground">{t.category || "—"} · {t.urgency}</div>
                        </div>
                        <div className="font-mono-plex text-xs text-foreground/80">£{Number(t.default_price).toFixed(0)}</div>
                      </button>
                    ))}
                  {templates.filter(t => t.name.toLowerCase().includes(tplQuery.toLowerCase())).length === 0 && (
                    <div className="px-3 py-4 text-center text-xs text-muted-foreground">No matches — fill the form below manually.</div>
                  )}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-sm font-medium">Name *</label>
                <input required value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} className="input-default mt-1" placeholder="Front brake pads" data-testid="item-name-input" />
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium">Description</label>
                <textarea rows={2} value={newItem.description} onChange={(e) => setNewItem({ ...newItem, description: e.target.value })} className="input-default mt-1" data-testid="item-description-input" />
              </div>
              <div>
                <label className="text-sm font-medium">Quantity</label>
                <input type="number" step="0.1" min="0" value={newItem.quantity} onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value })} className="input-default mt-1" data-testid="item-quantity-input" />
              </div>
              <div>
                <label className="text-sm font-medium">Fitted price (£)</label>
                <input type="number" step="0.01" min="0" value={newItem.unit_price} onChange={(e) => setNewItem({ ...newItem, unit_price: e.target.value })} className="input-default mt-1" placeholder="Parts + labour combined" data-testid="item-price-input" />
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium">Explainer video URL <span className="text-muted-foreground font-normal">(optional, YouTube)</span></label>
                <input type="url" value={newItem.explainer_url} onChange={(e) => setNewItem({ ...newItem, explainer_url: e.target.value })} className="input-default mt-1" placeholder="https://youtu.be/… or Vehicle Visuals link" data-testid="item-explainer-input" />
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium">Traffic-light grading</label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {URGENCIES.map(u => (
                    <button key={u.v} type="button" onClick={() => setNewItem({ ...newItem, urgency: u.v })}
                      className={`px-2 py-2 rounded-lg border-2 text-xs font-bold uppercase tracking-wider transition-all ${
                        newItem.urgency === u.v ? u.color + " ring-2 ring-offset-2 ring-offset-card" : "border-border hover:bg-muted text-muted-foreground"
                      }`} data-testid={`item-urgency-${u.v}`}>
                      <div>{u.label}</div>
                      <div className="text-[9px] font-medium opacity-80 normal-case tracking-normal">{u.sub}</div>
                    </button>
                  ))}
                </div>
              </div>
              {newItem.urgency === "amber" && (
                <div className="col-span-2" data-testid="item-recommend-in-block">
                  <label className="text-sm font-medium">
                    Recommend in <span className="text-muted-foreground font-normal">(optional — pre-highlights the snooze chip if customer declines)</span>
                  </label>
                  <div className="grid grid-cols-4 gap-2 mt-1">
                    {[null, 3, 6, 9].map((m) => (
                      <button
                        key={String(m)}
                        type="button"
                        onClick={() => setNewItem({ ...newItem, recommended_in_months: m })}
                        className={`px-2 py-2 rounded-lg border text-xs font-bold transition-all ${
                          newItem.recommended_in_months === m
                            ? "border-warning bg-warning/15 text-warning ring-2 ring-warning/30"
                            : "border-border hover:bg-muted text-muted-foreground"
                        }`}
                        data-testid={`item-recommend-${m === null ? "none" : `${m}m`}`}
                      >
                        {m === null ? "No default" : `${m} months`}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {/* Tyre tier upsell — visible when the item looks like a tyre.
                  Lets the advisor offer a Budget alternative the customer can
                  switch to from the public quote page. */}
              {/tyre|tire/i.test(newItem.name) && (
                <div className="col-span-2 rounded-lg border border-primary/30 bg-primary/[0.04] p-3 space-y-2" data-testid="tyre-tier-block">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-bold">Offer a Budget alternative</div>
                    <span className="text-[10px] font-mono-plex uppercase tracking-wider text-primary">Tyre upsell</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    The customer sees a two-card chooser on the quote — your <strong>Recommended</strong> (above) vs this <strong>Budget</strong> option.
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="col-span-2">
                      <label className="text-xs font-medium">Budget tyre name</label>
                      <input
                        type="text"
                        value={newItem.tyre_budget_name}
                        onChange={(e) => setNewItem({ ...newItem, tyre_budget_name: e.target.value })}
                        className="input-default mt-1"
                        placeholder="e.g. Linglong RT 205/55 R16"
                        data-testid="tyre-budget-name-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium">Budget price (£)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={newItem.tyre_budget_price}
                        onChange={(e) => setNewItem({ ...newItem, tyre_budget_price: e.target.value })}
                        className="input-default mt-1"
                        placeholder="79.00"
                        data-testid="tyre-budget-price-input"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowAddItem(false)} className="btn-outline" data-testid="add-item-cancel-btn">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="add-item-save-btn">Add item</button>
            </div>
          </form>
        </div>
      )}

      <VHCImportModal
        jobId={id}
        open={showVHC}
        onClose={() => setShowVHC(false)}
        onImported={(vehicle) => {
          // If the VHC detected a vehicle and the job is missing it, populate
          if (vehicle && (!job.vehicle_make || !job.vehicle_reg)) {
            const patch = {};
            if (vehicle.make && !job.vehicle_make) patch.vehicle_make = vehicle.make;
            if (vehicle.model && !job.vehicle_model) patch.vehicle_model = vehicle.model;
            if (vehicle.year && !job.vehicle_year) patch.vehicle_year = vehicle.year;
            if (vehicle.registration && !job.vehicle_reg) patch.vehicle_reg = vehicle.registration;
            if (Object.keys(patch).length) api.patch(`/jobs/${id}`, patch).catch(() => {});
          }
          refresh();
        }}
      />

      <VhcChecklistModal
        jobId={id}
        open={showVhcChecklist}
        onClose={() => setShowVhcChecklist(false)}
        onSaved={refresh}
        vehicle={[job.vehicle_year, job.vehicle_make, job.vehicle_model].filter(Boolean).join(" ")}
      />

      <SettingsModal open={showSettings} onClose={() => setShowSettings(false)} />
      <HandoverModal
        open={showHandover}
        jobId={id}
        onClose={() => setShowHandover(false)}
        onCompleted={() => refresh()}
      />

      <StatusUpdateModal
        open={showStatusUpdate}
        onClose={() => setShowStatusUpdate(false)}
        job={job}
        onSent={() => refresh()}
      />

      {/* Resend confirmation modal — lets the advisor amend the customer's
          phone/email before queuing the 5-minute delayed dispatch. */}
      {showResend && (
        <div
          className="fixed inset-0 z-50 bg-foreground/60 backdrop-blur-sm grid place-items-center px-4"
          onClick={() => !resendBusy && setShowResend(false)}
          data-testid="resend-modal"
        >
          <div
            className="bg-background border border-border rounded-xl shadow-2xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="label-overline text-primary">Resend quote</div>
                <div className="font-display font-bold text-xl mt-1">Confirm where it goes</div>
              </div>
              <button
                type="button"
                onClick={() => setShowResend(false)}
                disabled={resendBusy}
                className="text-muted-foreground hover:text-foreground"
                aria-label="Close"
                data-testid="resend-modal-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
              Fires <span className="font-semibold text-foreground">5 minutes</span> after you confirm — plenty of time to cancel if the customer gives a different number.
            </p>
            <div className="mt-5 space-y-3">
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                  <Phone className="w-3 h-3" /> Mobile (SMS)
                </span>
                <input
                  type="tel"
                  value={resendPhone}
                  onChange={(e) => setResendPhone(e.target.value)}
                  placeholder="+44 7700 900000"
                  className="input-default mt-1"
                  data-testid="resend-phone-input"
                  disabled={resendBusy}
                />
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                  <Mail className="w-3 h-3" /> Email
                </span>
                <input
                  type="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="customer@example.com"
                  className="input-default mt-1"
                  data-testid="resend-email-input"
                  disabled={resendBusy}
                />
              </label>
            </div>
            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowResend(false)}
                disabled={resendBusy}
                className="btn-outline"
                data-testid="resend-cancel-modal-btn"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitResend}
                disabled={resendBusy || (!resendPhone.trim() && !resendEmail.trim())}
                className="btn-primary"
                data-testid="resend-confirm-btn"
              >
                {resendBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />}
                Queue 5-min resend
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * EMaC plan badge — looks the vehicle reg up via /api/jobs/{id}/emac and,
 * when an active Service Plan exists, renders a yellow banner so the advisor
 * sees it BEFORE quoting. Read-only Phase-1 surface — does not auto-zero
 * covered items (that's gated behind a future setting once the matching
 * logic has been eyeballed against real lookups).
 */
function EMaCPlanBadge({ jobId, reg, onCoverageApplied }) {
  const [state, setState] = useState({ loading: true, plan: null });
  const [coverage, setCoverage] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!reg) { setState({ loading: false, plan: null }); return; }
    setState({ loading: true, plan: null });
    api.get(`/jobs/${jobId}/emac`)
      .then((r) => {
        if (cancelled) return;
        if (r.data?.configured === false) setState({ loading: false, plan: null, notConfigured: true });
        else if (r.data?.found) setState({ loading: false, plan: r.data });
        else setState({ loading: false, plan: null, checked: true });
      })
      .catch(() => { if (!cancelled) setState({ loading: false, plan: null, error: true }); });
    return () => { cancelled = true; };
  }, [jobId, reg]);

  // Phase 2 — fetch the coverage preview once the plan resolves so the
  // advisor can see at a glance how many items the plan would zero out.
  useEffect(() => {
    let cancelled = false;
    if (!state.plan?.is_live) { setCoverage(null); return; }
    api.get(`/jobs/${jobId}/emac/coverage`)
      .then((r) => { if (!cancelled) setCoverage(r.data); })
      .catch(() => { if (!cancelled) setCoverage(null); });
    return () => { cancelled = true; };
  }, [jobId, state.plan]);

  const applyCoverage = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/jobs/${jobId}/emac/apply-coverage`);
      toast.success(`Applied — ${r.data.applied} item${r.data.applied === 1 ? "" : "s"} covered by the plan`);
      onCoverageApplied?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't apply coverage");
    } finally {
      setBusy(false);
    }
  };

  const revertCoverage = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/jobs/${jobId}/emac/revert-coverage`);
      toast.success(`Reverted — ${r.data.reverted} item${r.data.reverted === 1 ? "" : "s"} restored`);
      onCoverageApplied?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't revert coverage");
    } finally {
      setBusy(false);
    }
  };

  if (state.loading) {
    return (
      <div className="mt-3 inline-flex items-center gap-2 text-xs text-muted-foreground" data-testid="emac-badge-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking for a service plan…
      </div>
    );
  }
  if (state.notConfigured || state.error || !state.plan) return null;

  const p = state.plan;
  const dateStr = p.action_date ? new Date(p.action_date).toLocaleDateString("en-GB", { month: "short", year: "numeric" }) : "";
  const coveredCount = coverage?.matches?.length || 0;
  const coveredValue = coverage?.total_covered_value || 0;
  const alreadyApplied = coveredCount === 0 && (coverage?.skipped_items || []).some((s) => s.reason === "already_zero");

  return (
    <div className="mt-3 rounded-lg border border-warning/40 bg-warning/[0.08] p-3 flex items-start gap-2.5" data-testid="emac-plan-badge">
      <ShieldCheck className="w-5 h-5 text-warning shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center flex-wrap gap-x-2 gap-y-1">
          <span className="text-[10px] font-bold uppercase tracking-wider text-warning bg-warning/15 border border-warning/30 px-1.5 py-0.5 rounded">
            Service plan active
          </span>
          <span className="text-xs font-mono-plex text-muted-foreground">{p.reference}</span>
        </div>
        <div className="mt-1 text-sm font-semibold leading-tight">{p.campaign_name}</div>
        <div className="mt-1 text-xs text-muted-foreground">
          {p.customer_name && <span>Held by <strong className="text-foreground">{p.customer_name}</strong> · </span>}
          {dateStr && <span>activated {dateStr} · </span>}
          {p.dealer_name}
        </div>

        {/* Phase 2 — coverage preview + apply CTA */}
        {coveredCount > 0 && (
          <div className="mt-3 rounded-md bg-warning/10 border border-warning/30 p-2.5" data-testid="emac-coverage-preview">
            <div className="text-xs font-bold text-warning uppercase tracking-wider">
              Coverage detected · {coveredCount} item{coveredCount === 1 ? "" : "s"} totalling £{coveredValue.toFixed(2)}
            </div>
            <ul className="mt-1.5 text-[11px] text-foreground/80 space-y-0.5">
              {coverage.matches.slice(0, 4).map((m) => (
                <li key={m.item_id}>· {m.item_name} <span className="text-muted-foreground">(£{m.original_price.toFixed(2)})</span></li>
              ))}
              {coverage.matches.length > 4 && (
                <li className="text-muted-foreground italic">…and {coverage.matches.length - 4} more</li>
              )}
            </ul>
            <button
              type="button"
              onClick={applyCoverage}
              disabled={busy}
              className="btn-primary mt-2.5 text-xs px-3 py-1.5 h-auto"
              data-testid="emac-apply-coverage-btn"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
              Apply plan coverage · zero £{coveredValue.toFixed(2)}
            </button>
            <p className="mt-1.5 text-[10px] text-muted-foreground">
              Sets the unit price to £0 on covered items. Reversible. Brake pads, tyres &amp; wear items are never zeroed.
            </p>
          </div>
        )}
        {alreadyApplied && (
          <div className="mt-3 rounded-md bg-success/10 border border-success/30 p-2.5" data-testid="emac-coverage-applied">
            <div className="text-xs font-bold text-success uppercase tracking-wider">
              Plan coverage already applied
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Covered items are showing as £0 on the quote. Tap below to undo.
            </p>
            <button
              type="button"
              onClick={revertCoverage}
              disabled={busy}
              className="btn-outline mt-2 text-xs px-3 py-1.5 h-auto"
              data-testid="emac-revert-coverage-btn"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              Revert coverage
            </button>
          </div>
        )}
        {coveredCount === 0 && !alreadyApplied && (
          <div className="mt-2 text-[11px] text-warning">
            ⚠ Check what's covered before quoting — the customer may already have paid for this service.
          </div>
        )}
      </div>
    </div>
  );
}


/**
 * Toyota Fixed Price Service auto-populate panel.
 *
 * Looks up the vehicle's class via /api/toyota-pricing/lookup and renders a
 * one-tap "Use £305 Intermediate / £465 Full" chip pair. Only shows up when
 * the model matches — non-Toyotas (or unknown models) get nothing.
 */
function ToyotaPricingPanel({ jobId, make, model, currentName, currentAmount, onApplied }) {
  const [match, setMatch] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lookupDone, setLookupDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!make || !model) { setMatch(null); setLookupDone(true); return; }
    api.get(`/toyota-pricing/lookup`, { params: { make, model } })
      .then((r) => { if (!cancelled) setMatch(r.data?.matched ? r.data : null); })
      .catch(() => { if (!cancelled) setMatch(null); })
      .finally(() => { if (!cancelled) setLookupDone(true); });
    return () => { cancelled = true; };
  }, [make, model]);

  if (!lookupDone || !match) return null;

  const apply = async (tier) => {
    setBusy(true);
    try {
      const r = await api.post(`/jobs/${jobId}/apply-toyota-pricing`, { tier });
      onApplied?.(r.data);
      toast.success(`Booked service set: ${r.data.name} · £${r.data.amount.toFixed(2)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't apply Toyota price");
    } finally { setBusy(false); }
  };

  const isApplied = (tier) => {
    if (!currentName) return false;
    const price = tier === "full" ? match.full : match.intermediate;
    return currentAmount === price && /Toyota/.test(currentName) && new RegExp(tier, "i").test(currentName);
  };

  return (
    <div className="mt-3 rounded-lg border border-primary/30 bg-primary/[0.04] p-3 space-y-2" data-testid="toyota-pricing-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-primary" />
          <div>
            <div className="text-sm font-bold">Toyota Fixed Price Service</div>
            <div className="text-[11px] text-muted-foreground">Matched <span className="font-semibold text-foreground">{match.class_label}</span> · auto-fills the line above</div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {[
          { tier: "intermediate", label: "Intermediate", price: match.intermediate },
          { tier: "full", label: "Full", price: match.full },
        ].filter((opt) => opt.price != null).map((opt) => {
          const active = isApplied(opt.tier);
          return (
            <button
              key={opt.tier}
              type="button"
              disabled={busy}
              onClick={() => apply(opt.tier)}
              className={`text-left rounded-md border-2 px-3 py-2 transition-all ${
                active ? "border-primary bg-primary/[0.08] ring-2 ring-primary/20" : "border-border hover:border-primary/40 hover:bg-primary/[0.04]"
              } ${busy ? "opacity-60 cursor-not-allowed" : ""}`}
              data-testid={`toyota-tier-${opt.tier}-btn`}
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{opt.label}</span>
                {active && <Check className="w-3.5 h-3.5 text-primary" />}
              </div>
              <div className="mt-1 text-base font-bold font-mono-plex">£{opt.price.toFixed(2)}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}


/**
 * Geely Fixed Service Pricing panel — mirrors ToyotaPricingPanel.
 * Looks up the vehicle's powertrain (EV / Hybrid) via /api/geely-pricing/lookup
 * and renders one-tap mileage-tier buttons. Hidden for non-Geely models.
 */
function GeelyPricingPanel({ jobId, make, model, currentName, currentAmount, onApplied }) {
  const [match, setMatch] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lookupDone, setLookupDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!make || !model) { setMatch(null); setLookupDone(true); return; }
    api.get(`/geely-pricing/lookup`, { params: { make, model } })
      .then((r) => { if (!cancelled) setMatch(r.data?.matched ? r.data : null); })
      .catch(() => { if (!cancelled) setMatch(null); })
      .finally(() => { if (!cancelled) setLookupDone(true); });
    return () => { cancelled = true; };
  }, [make, model]);

  if (!lookupDone || !match) return null;

  const apply = async (tierKey) => {
    setBusy(true);
    try {
      const r = await api.post(`/jobs/${jobId}/apply-geely-pricing`, { tier: tierKey });
      onApplied?.(r.data);
      toast.success(`Booked service set: ${r.data.name} · £${r.data.amount.toFixed(2)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't apply Geely price");
    } finally { setBusy(false); }
  };

  const isApplied = (t) =>
    !!currentName && /Geely/.test(currentName) && currentAmount === t.amount &&
    currentName.includes(t.label);

  return (
    <div className="mt-3 rounded-lg border border-primary/30 bg-primary/[0.04] p-3 space-y-2" data-testid="geely-pricing-panel">
      <div className="flex items-center gap-2">
        <Zap className="w-4 h-4 text-primary" />
        <div>
          <div className="text-sm font-bold">Geely Fixed Service Pricing</div>
          <div className="text-[11px] text-muted-foreground">
            Matched <span className="font-semibold text-foreground">{match.powertrain_label}</span> schedule · auto-fills the line above
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {match.tiers.map((t) => {
          const active = isApplied(t);
          return (
            <button
              key={t.key}
              type="button"
              disabled={busy}
              onClick={() => apply(t.key)}
              className={`text-left rounded-md border-2 px-3 py-2 transition-all ${
                active ? "border-primary bg-primary/[0.08] ring-2 ring-primary/20" : "border-border hover:border-primary/40 hover:bg-primary/[0.04]"
              } ${busy ? "opacity-60 cursor-not-allowed" : ""}`}
              data-testid={`geely-tier-${t.key}-btn`}
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{t.label}</span>
                {active && <Check className="w-3.5 h-3.5 text-primary" />}
              </div>
              <div className="mt-1 text-base font-bold font-mono-plex">£{t.amount.toFixed(2)}</div>
            </button>
          );
        })}
      </div>
      <div className="text-[10px] text-muted-foreground">Geely UK published prices · correct as of {match.prices_as_of}</div>
    </div>
  );
}



/**
 * Toyota Repair Pricing (TRP 2026) quick-add chips.
 *
 * Looks up the vehicle's repair-price class via /api/toyota-pricing/repairs
 * and renders one-tap chips ("Front brake pads £195") that add a priced amber
 * item to the quote. Hidden for non-Toyotas / unknown models.
 */
function ToyotaRepairPricingPanel({ jobId, make, model, items, onAdded }) {
  const [match, setMatch] = useState(null);
  const [open, setOpen] = useState(false);
  const [busyKey, setBusyKey] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!make || !model) { setMatch(null); return; }
    api.get(`/toyota-pricing/repairs`, { params: { make, model } })
      .then((r) => { if (!cancelled) setMatch(r.data?.matched ? r.data : null); })
      .catch(() => { if (!cancelled) setMatch(null); });
    return () => { cancelled = true; };
  }, [make, model]);

  if (!match || !(match.repairs || []).length) return null;

  const alreadyAdded = (name) => (items || []).some((it) => (it.name || "").toLowerCase() === name.toLowerCase());

  const add = async (rep) => {
    setBusyKey(rep.key);
    try {
      await api.post(`/jobs/${jobId}/items`, {
        type: "labor",
        name: rep.name,
        description: `Toyota fixed repair price 2026 · ${match.class_label}`,
        quantity: 1,
        unit_price: rep.price,
        urgency: "amber",
      });
      toast.success(`${rep.name} added — £${rep.price.toFixed(2)}`);
      onAdded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add repair");
    } finally { setBusyKey(null); }
  };

  return (
    <div className="mt-4 rounded-lg border border-primary/30 bg-primary/[0.04] p-3" data-testid="toyota-repair-pricing-panel">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-2 text-left" data-testid="toyota-repair-pricing-toggle">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-primary" />
          <div>
            <div className="text-sm font-bold">Toyota Fixed Repair Prices (2026)</div>
            <div className="text-[11px] text-muted-foreground">Matched <span className="font-semibold text-foreground">{match.class_label}</span> · tap to add a priced line</div>
          </div>
        </div>
        <span className="text-xs font-bold text-primary">{open ? "Hide" : `Show ${match.repairs.length}`}</span>
      </button>
      {open && (
        <>
          {match.needs_confirmation && (
            <div className="mt-2 text-[11px] font-semibold text-warning" data-testid="toyota-repair-confirm-note">
              GR-range menu — double-check against the printed TRP sheet before quoting.
            </div>
          )}
          {match.note && <div className="mt-1 text-[11px] text-muted-foreground">{match.note}</div>}
          <div className="mt-2 flex flex-wrap gap-2">
            {match.repairs.map((rep) => {
              const added = alreadyAdded(rep.name);
              return (
                <button
                  key={rep.key}
                  type="button"
                  disabled={busyKey === rep.key || added}
                  onClick={() => add(rep)}
                  className={`text-xs font-bold inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border transition-colors ${
                    added
                      ? "border-success/40 bg-success/10 text-success cursor-default"
                      : "border-border hover:border-primary/50 hover:bg-primary/[0.06]"
                  }`}
                  data-testid={`toyota-repair-chip-${rep.key}`}
                >
                  {added ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                  {rep.name} · <span className="font-mono-plex">£{rep.price.toFixed(0)}</span>
                </button>
              );
            })}
          </div>
          <div className="mt-2 text-[10px] text-muted-foreground">All prices include VAT · TRP 2026 v1.0 · vehicles from 2009 onwards</div>
        </>
      )}
    </div>
  );
}


// ---------- Resend countdown banner ----------
function ResendCountdownBanner({ scheduledAt, phone, email, nowTick, onCancel }) {
  const remaining = Math.max(0, new Date(scheduledAt).getTime() - nowTick);
  const mins = Math.floor(remaining / 60000);
  const secs = Math.floor((remaining % 60000) / 1000);
  return (
    <div
      className="max-w-7xl xl:max-w-[1500px] 2xl:max-w-[1720px] mx-auto px-6 py-3 flex items-center gap-3 bg-warning/10 border-y border-warning/30"
      data-testid="resend-pending-banner"
    >
      <Clock className="w-4 h-4 text-warning shrink-0" />
      <div className="flex-1 min-w-0 text-sm">
        <span className="font-semibold text-warning">Resend queued</span>
        <span className="text-muted-foreground"> — fires in </span>
        <span className="font-mono-plex font-bold text-warning" data-testid="resend-countdown">
          {String(mins).padStart(2, "0")}:{String(secs).padStart(2, "0")}
        </span>
        <span className="text-muted-foreground hidden sm:inline">
          {" "}· to {[phone, email].filter(Boolean).join(" + ") || "(no contacts)"}
        </span>
      </div>
      <button
        type="button"
        onClick={onCancel}
        className="btn-outline border-warning/40 text-warning hover:bg-warning/5"
        data-testid="resend-cancel-btn"
      >
        <X className="w-4 h-4" /> Cancel
      </button>
    </div>
  );
}



/**
 * Inline editor for a line-item's explainer video URL. Sits directly under
 * the item description on the job editor. When no URL is set it shows a
 * subtle "+ Add explainer video" prompt; when a URL is set it shows a
 * pill with edit + open + clear. Persists via `PATCH /jobs/{id}/items/{itemId}`.
 *
 * Accepts any URL (YouTube auto-embeds on the customer page; other providers
 * — Vehicle Visuals, Vimeo, dealer-hosted MP4s — render as a "Watch video"
 * card that opens in a new tab).
 */
function ItemExplainerEditor({ jobId, item, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.explainer_url || "");
  const [saving, setSaving] = useState(false);

  const save = async (nextUrl) => {
    const clean = (nextUrl ?? draft).trim();
    if (clean === (item.explainer_url || "")) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/jobs/${jobId}/items/${item.id}`, {
        explainer_url: clean || null,
      });
      onSaved({ explainer_url: clean || null });
      toast.success(clean ? "Video link attached" : "Video link removed");
      setEditing(false);
    } catch {
      toast.error("Couldn't save video link");
    } finally {
      setSaving(false);
    }
  };

  if (!editing && !item.explainer_url) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="inline-flex items-center gap-1 mt-1.5 text-xs text-muted-foreground hover:text-primary transition-colors"
        data-testid={`item-explainer-add-${item.id}`}
      >
        <Video className="w-3 h-3" /> + Add explainer video
      </button>
    );
  }

  if (!editing && item.explainer_url) {
    return (
      <div className="inline-flex items-center gap-1.5 mt-1.5 text-xs" data-testid={`item-explainer-attached-${item.id}`}>
        <a
          href={item.explainer_url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-primary hover:underline"
        >
          <Video className="w-3 h-3" /> Explainer video attached
        </a>
        <button
          type="button"
          onClick={() => { setDraft(item.explainer_url); setEditing(true); }}
          className="text-muted-foreground hover:text-primary"
          title="Edit video link"
          data-testid={`item-explainer-edit-${item.id}`}
        >
          <Edit2 className="w-3 h-3" />
        </button>
        <button
          type="button"
          onClick={() => save("")}
          className="text-muted-foreground hover:text-destructive"
          title="Remove video link"
          disabled={saving}
          data-testid={`item-explainer-clear-${item.id}`}
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    );
  }

  return (
    <div className="mt-1.5 flex items-center gap-2 flex-wrap" data-testid={`item-explainer-editor-${item.id}`}>
      <input
        type="url"
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          if (e.key === "Escape") { setDraft(item.explainer_url || ""); setEditing(false); }
        }}
        placeholder="https://youtu.be/… or Vehicle Visuals link"
        className="input-default text-xs py-1 flex-1 min-w-[220px]"
        data-testid={`item-explainer-input-${item.id}`}
      />
      <button
        type="button"
        onClick={() => save()}
        disabled={saving}
        className="btn-primary btn-sm text-xs"
        data-testid={`item-explainer-save-${item.id}`}
      >
        {saving ? "Saving…" : "Save"}
      </button>
      <button
        type="button"
        onClick={() => { setDraft(item.explainer_url || ""); setEditing(false); }}
        disabled={saving}
        className="text-xs text-muted-foreground hover:text-foreground"
        data-testid={`item-explainer-cancel-${item.id}`}
      >
        Cancel
      </button>
    </div>
  );
}
